import React, { useEffect, useState } from "react";
import gql from "graphql-tag";
import { useLazyQuery, useQuery } from "@apollo/client";
import styled from "styled-components";
import CalendarUI from "../UI/Calendar/CalendarUI";
import moment from "moment";
import { ScrollBar } from "../../Theme/Hyper";
import {
  parseCategories,
  getLoggingCategories,
  calcTotalVariable,
  calTotal,
  calcTotalStrikesThrown as calcStrikesThrown,
  calcTotalStrikesLanded as calcStrikesLanded,
} from "../../utils/CategoryUtils";
const GAMES_BY_DATE = gql`
  query Games($date: String) {
    gamesByDate(date: $date) {
      _id
      time
      description
      date
      competition {
        _id
        oval_ID
        name
        competition_type
        badge
        secondary_badge
        flag_path
        fighter_path
        fighter_full_path
        categories
      }
      home_fighter {
        _id
        first_name
        last_name
        short_name
        image
        image_2
        stories
        corner
        shorts_colour
        height
        weight
        reach
        legreach
        style
        age
        nationality {
          code
        }
      }
      away_fighter {
        _id
        first_name
        last_name
        short_name
        image
        image_2
        stories
        corner
        shorts_colour
        height
        weight
        reach
        legreach
        style
        age
        nationality {
          code
        }
      }
      home_player_stats
      away_player_stats
      number_of_rounds
      gold_fight
      title
      championship_bar
      round_minutes
      winner
      winnerby
      completed
      live_data
    }
  }
`;

const GAMES_BY_MONTH = gql`
  query Games($date: String) {
    gamesByMonth(date: $date) {
      _id
      date
    }
  }
`;
const Main = styled.div`
  display: flex;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
  width: 100%;
  height: 100%;
`;
const GamesDiv = styled(ScrollBar)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1em;
  box-sizing: border-box;
  overflow-y: auto;
  height: 95vh;
`;
const Game = styled.div`
  display: flex;
  width: 100%;
  padding: 0.5em;
  justify-content: center;
  font-size: 1em;
  font-weight: 100;
  align-items: center;
  box-sizing: border-box;
  background-color: ${(props) =>
    props.sub ? " #181d25" : props.selected ? "#db0a41" : "#202731"};
  border-bottom: 2px solid #181d25;
  cursor: ${(props) => (props.clickable ? "pointer" : "")};
  :hover {
    ${(props) => !props.noHover && !props.selected && { color: "#d11e42" }}
  }
`;
const Team = styled.div`
  display: flex;
  margin: 0.5em;
  width: 45%;
  justify-content: ${(props) => (props.right ? "flex-end" : "")};
`;
const Verses = styled.div`
  width: 10%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Competitions = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #202731;
  margin-left: 1px;
  width: ${(props) => (props.iPad ? "500px" : "300px")};
  padding: 1em;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

// Default categories for backward compatibility with Sotic export
const DEFAULT_CATS = [
  "Landed Arm Strikes",
  "Landed Leg Strikes",
  "Landed Ground Strikes",
  "Takedown Success",
  "Submission Success",
  "Missed Arm Strikes",
  "Missed Leg Strikes",
  "Missed Ground Strikes",
  "Takedown Attempted",
  "Submission Attempted",
  "Knockdowns",
];

function Data() {
  const [getFixtures, { data }] = useLazyQuery(GAMES_BY_DATE, {
    fetchPolicy: "network-only",
  });
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [fixtures, setFixtures] = useState([]);
  const [monthFixtures, setMonthFixtures] = useState();
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [getMonthFixtures, { data: month_data }] = useLazyQuery(
    GAMES_BY_MONTH,
    {
      fetchPolicy: "network-only",
    },
  );

  // Get CATEGORIES from game or use defaults
  function getCategories(game) {
    return game?.competition?.categories?.length > 0
      ? game.competition.categories
      : DEFAULT_CATS;
  }

  function calTotalLocal({ stat_1, stat_2, stats, round }) {
    return calTotal({ stat_1, stat_2, stats, round });
  }

  function calcTotalStrikesThrown({ stats, categories, round }) {
    return calcStrikesThrown({ stats, categories, round });
  }

  function calcTotalStrikesLanded({ stats, categories, round }) {
    return calcStrikesLanded({ stats, categories, round });
  }

  function calcTotalVariableLocal({ variable, stats, round }) {
    return calcTotalVariable({ variable, stats, round });
  }
  useEffect(() => {
    if (data) {
      let obj = data.gamesByDate?.map((game) => {
        const CATEGORIES = getCategories(game);
        const parsedCats = parseCategories(game?.competition?.categories);

        // Build dynamic stats from parsed categories
        const buildDynamicStats = (stats, round) => {
          const dynamicStats = {};
          parsedCats.forEach((cat, index) => {
            const catKey = cat.name.replace(/\s+/g, "");
            dynamicStats[`${catKey}Landed`] = calcTotalVariableLocal({
              variable: cat.landed,
              stats,
              round,
            });
            dynamicStats[`${catKey}Thrown`] = calTotalLocal({
              stat_1: cat.landed,
              stat_2: cat.missed,
              stats,
              round,
            });
          });
          return dynamicStats;
        };

        return {
          Id: game?._id,
          title: game?.title,
          winner: game?.winner?._id,
          winnerBy: game?.winnerby,
          round: Math.min(
            [...(game?.live_data?.periods || [])].sort((a, b) => b.number - a.number)[0]?.number || 0,
            3,
          ),
          Fighters: [
            {
              Id: game?.home_fighter?._id,
              Corner: "Red",
              height: game?.home_fighter?.height,
              FirstName: game?.home_fighter?.first_name,
              LastName: game?.home_fighter?.last_name,
              nationality: game?.home_fighter?.nationality?.code,
              weight: game?.home_fighter?.weight,
              style: game?.home_fighter?.style,
              reach: game?.home_fighter?.reach,
              leg_reach: game?.home_fighter?.legreach,
              age: game?.home_fighter?.age,
              image: game?.home_fighter?.image_2,
              FightStats: {
                StrikesLanded: calcTotalStrikesLanded({
                  stats: game?.home_player_stats,
                  categories: CATEGORIES,
                }),
                StrikesThrown: calcTotalStrikesThrown({
                  stats: game?.home_player_stats,
                  categories: CATEGORIES,
                }),
                ...buildDynamicStats(game?.home_player_stats),
              },
              RoundStats: [
                {
                  Round: 1,
                  StrikesLanded: calcTotalStrikesLanded({
                    stats: game?.home_player_stats,
                    categories: CATEGORIES,
                    round: 1,
                  }),
                  StrikesThrown: calcTotalStrikesThrown({
                    stats: game?.home_player_stats,
                    categories: CATEGORIES,
                    round: 1,
                  }),
                  ...buildDynamicStats(game?.home_player_stats, 1),
                },
                {
                  Round: 2,
                  StrikesLanded: calcTotalStrikesLanded({
                    stats: game?.home_player_stats,
                    categories: CATEGORIES,
                    round: 2,
                  }),
                  StrikesThrown: calcTotalStrikesThrown({
                    stats: game?.home_player_stats,
                    categories: CATEGORIES,
                    round: 2,
                  }),
                  ...buildDynamicStats(game?.home_player_stats, 2),
                },
                {
                  Round: 3,
                  StrikesLanded: calcTotalStrikesLanded({
                    stats: game?.home_player_stats,
                    categories: CATEGORIES,
                    round: 3,
                  }),
                  StrikesThrown: calcTotalStrikesThrown({
                    stats: game?.home_player_stats,
                    categories: CATEGORIES,
                    round: 3,
                  }),
                  ...buildDynamicStats(game?.home_player_stats, 3),
                },
              ],
            },
            {
              Id: game?.away_fighter?._id,
              Corner: "Blue",
              height: game?.away_fighter?.height,
              weight: game?.away_fighter?.weight,
              FirstName: game?.away_fighter?.first_name,
              LastName: game?.away_fighter?.last_name,
              nationality: game?.away_fighter?.nationality?.code,
              style: game?.away_fighter?.style,
              reach: game?.away_fighter?.reach,
              leg_reach: game?.away_fighter?.legreach,
              age: game?.away_fighter?.age,
              image: game?.away_fighter?.image_2,
              FightStats: {
                StrikesLanded: calcTotalStrikesLanded({
                  stats: game?.away_player_stats,
                  categories: CATEGORIES,
                }),
                StrikesThrown: calcTotalStrikesThrown({
                  stats: game?.away_player_stats,
                  categories: CATEGORIES,
                }),
                ...buildDynamicStats(game?.away_player_stats),
              },
              RoundStats: [
                {
                  Round: 1,
                  StrikesLanded: calcTotalStrikesLanded({
                    stats: game?.away_player_stats,
                    categories: CATEGORIES,
                    round: 1,
                  }),
                  StrikesThrown: calcTotalStrikesThrown({
                    stats: game?.away_player_stats,
                    categories: CATEGORIES,
                    round: 1,
                  }),
                  ...buildDynamicStats(game?.away_player_stats, 1),
                },
                {
                  Round: 2,
                  StrikesLanded: calcTotalStrikesLanded({
                    stats: game?.away_player_stats,
                    categories: CATEGORIES,
                    round: 2,
                  }),
                  StrikesThrown: calcTotalStrikesThrown({
                    stats: game?.away_player_stats,
                    categories: CATEGORIES,
                    round: 2,
                  }),
                  ...buildDynamicStats(game?.away_player_stats, 2),
                },
                {
                  Round: 3,
                  StrikesLanded: calcTotalStrikesLanded({
                    stats: game?.away_player_stats,
                    categories: CATEGORIES,
                    round: 3,
                  }),
                  StrikesThrown: calcTotalStrikesThrown({
                    stats: game?.away_player_stats,
                    categories: CATEGORIES,
                    round: 3,
                  }),
                  ...buildDynamicStats(game?.away_player_stats, 3),
                },
              ],
            },
          ],
        };
      });

      fetch("https://pfl-data.hyper.live/data", {
        method: "POST",
        body: JSON.stringify(obj),
        headers: {
          "Content-Type": "application/json",
        },
      });
      setFixtures([...(data.gamesByDate || [])]);
    }
  }, [data]);

  useEffect(() => {
    if (date) {
      getFixtures({ variables: { date: date + "Z" } });
      let interval = setInterval(() => {
        getFixtures({ variables: { date: date + "Z" } });
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [date, getFixtures]);

  useEffect(() => {
    if (selectedMonth) {
      getMonthData();
    }
  }, [selectedMonth, getMonthFixtures]);

  useEffect(() => {
    if (month_data) {
      setMonthFixtures([...(month_data?.gamesByMonth || [])]);
    }
  }, [month_data]);

  function getMonthData() {
    getMonthFixtures({
      variables: { date: selectedMonth.format("YYYY-MM-DD") + "Z" },
    });
  }

  return (
    <Main>
      <Competitions iPad={false}>
        <CalendarUI
          onChangeDate={(date) => {
            setDate(moment(date).format("YYYY-MM-DD"));
          }}
          date={moment()}
          onActiveStartDateChange={({ activeStartDate, value }) => {
            setSelectedMonth(moment(activeStartDate));
          }}
          highlights={monthFixtures?.map((m) => m.date)}
        />
      </Competitions>
      <Column>
        <GamesDiv>
          {fixtures
            ?.sort((a, b) => {
              if (a.competition > b.competition) {
                return 1;
              } else if (a.competition < b.competition) {
                return -1;
              }

              let a_date = moment(a.date + " " + a.time);
              let b_date = moment(b.date + " " + b.time);
              // Else go to the 2nd item
              if (a_date < b_date) {
                return -1;
              } else if (a_date > b_date) {
                return 1;
              } else {
                // nothing to split them
                return 0;
              }
            })
            ?.map((fixture, index) => {
              return (
                <>
                  {(index === 0 ||
                    fixtures[index - 1].competition._id !==
                      fixture.competition._id) && (
                    <div>{fixture.competition?.name}</div>
                  )}
                  <Game
                    onClick={() => {
                      //   onSelectGame(fixture);
                      //   let path = location.pathname;
                      //   history.push({
                      //     pathname:
                      //       location.pathname.replace("/", "") +
                      //       "/" +
                      //       fixture._id,
                      //   });
                    }}
                  >
                    <Team right>
                      {fixture.home_fighter?.first_name}{" "}
                      {fixture.home_fighter?.last_name}
                    </Team>
                    <Verses>
                      <div>
                        Fight {index + 1} -{" "}
                        {moment("01/01/1990 " + fixture.time).format("HH:mm")}
                      </div>
                    </Verses>
                    <Team>
                      {fixture.away_fighter?.first_name}{" "}
                      {fixture.away_fighter?.last_name}
                    </Team>
                  </Game>
                </>
              );
            })}
        </GamesDiv>
      </Column>
    </Main>
  );
}

export default Data;
